import { Container, Graphics, Text, type Ticker } from 'pixi.js';

import { weatherLabels, type SeasonState, type WeatherType } from '../engine/seasons/SeasonTypes';

export class WeatherBadge {
  readonly root = new Container();
  private readonly icon = new Graphics();
  private readonly label = new Text({
    text: '',
    style: {
      fill: '#fff2cb',
      fontFamily: 'Georgia, serif',
      fontSize: 17,
      fontWeight: 'bold',
      stroke: { color: '#18200f', width: 4 },
    },
  });
  private weather?: WeatherType;
  private elapsed = 0;

  constructor() {
    this.root.scale.set(1.28);
    this.root.position.set(30, 78);
    this.label.position.set(76, 20);
    this.root.addChild(this.icon, this.label);
  }

  update(season: SeasonState, ticker: Ticker): void {
    if (this.weather !== season.currentWeather) {
      this.weather = season.currentWeather;
      this.draw(season.currentWeather);
      this.label.text = weatherLabels[season.currentWeather];
    }
    this.elapsed += ticker.deltaMS / 1000;
    const pulse = 1 + Math.sin(this.elapsed * 1.35) * 0.018;
    this.icon.scale.set(pulse);
    this.icon.y = Math.sin(this.elapsed * 0.9) * 1.2;
  }

  private draw(weather: WeatherType): void {
    this.icon.clear();
    const showSun = weather === 'clear' || weather === 'cloudy' || weather === 'frost';
    const showCloud = weather !== 'clear' && weather !== 'wind';

    if (showSun) this.drawSun();
    if (showCloud) this.drawCloud();

    if (weather === 'rain' || weather === 'storm') {
      this.icon
        .moveTo(21, 45).lineTo(17, 56)
        .moveTo(36, 45).lineTo(32, 56)
        .moveTo(51, 45).lineTo(47, 56)
        .stroke({ color: 0x6fc9ef, width: 4, cap: 'round' });
    }
    if (weather === 'storm') {
      this.icon
        .poly([38, 40, 31, 52, 39, 52, 34, 65, 49, 47, 41, 47])
        .fill(0xffd75a);
    }
    if (weather === 'snow' || weather === 'frost') {
      for (const [x, y] of [[20, 50], [36, 55], [52, 49]] as const) {
        this.icon
          .moveTo(x - 4, y).lineTo(x + 4, y)
          .moveTo(x, y - 4).lineTo(x, y + 4)
          .stroke({ color: 0xe8f8ff, width: 2, cap: 'round' });
      }
    }
    if (weather === 'fog') {
      this.icon
        .moveTo(8, 47).bezierCurveTo(22, 42, 40, 52, 62, 46)
        .moveTo(14, 57).bezierCurveTo(31, 52, 45, 61, 67, 55)
        .stroke({ color: 0xdce7e7, width: 4, cap: 'round' });
    }
    if (weather === 'wind') {
      this.icon
        .moveTo(4, 25).bezierCurveTo(20, 15, 37, 22, 50, 18)
        .moveTo(9, 38).bezierCurveTo(27, 28, 48, 42, 68, 31)
        .moveTo(22, 51).bezierCurveTo(36, 45, 51, 52, 63, 46)
        .stroke({ color: 0xe6f1ed, width: 5, cap: 'round' });
    }
  }

  private drawSun(): void {
    this.icon.circle(24, 22, 15).fill(0xffd65a);
    this.icon.circle(20, 18, 5).fill({ color: 0xfff5b0, alpha: 0.7 });
    this.icon.circle(24, 22, 19).stroke({ color: 0xffd65a, width: 3, alpha: 0.28 });
  }

  private drawCloud(): void {
    this.icon.roundRect(18, 28, 48, 20, 10).fill(0xdbe8ee);
    this.icon.circle(32, 29, 13).fill(0xf6fbfd);
    this.icon.circle(49, 27, 16).fill(0xeaf3f6);
    this.icon
      .moveTo(21, 45)
      .bezierCurveTo(34, 49, 53, 50, 64, 43)
      .stroke({ color: 0x93aab5, width: 2, alpha: 0.45 });
  }
}
